import React from 'react';
import './LoadingSpinner.css';

/**
 * Loading Spinner Component
 * Used as fallback for React.lazy Suspense
 */
const LoadingSpinner = ({ size = 'medium', message = 'Yükleniyor...' }) => {
    const sizeClass = `loading-spinner--${size}`;

    return (
        <div className={`loading-spinner ${sizeClass}`} role="status" aria-live="polite">
            <div className="loading-spinner__container">
                <div className="loading-spinner__circle">
                    <div className="loading-spinner__inner"></div>
                </div>
                {message && (
                    <p className="loading-spinner__message">{message}</p>
                )}
            </div>
        </div>
    );
};

/**
 * Full Page Loading - Used for lazy loaded routes
 */
export const PageLoader = () => (
    <div className="page-loader">
        <div className="page-loader__content">
            <div className="page-loader__logo">
                <i className="fas fa-cube"></i>
            </div>
            <div className="page-loader__spinner"></div>
            <p className="page-loader__text">Sayfa yükleniyor...</p>
        </div>
    </div>
);

/**
 * Skeleton Loader Components
 */
export const SkeletonText = ({ width = '100%', height = '1rem', className = '' }) => (
    <div
        className={`skeleton skeleton--text ${className}`}
        style={{ width, height }}
        aria-hidden="true"
    />
);

export const SkeletonCircle = ({ size = '40px', className = '' }) => (
    <div
        className={`skeleton skeleton--circle ${className}`}
        style={{ width: size, height: size }}
        aria-hidden="true"
    />
);

export const SkeletonRect = ({ width = '100%', height = '100px', className = '' }) => (
    <div
        className={`skeleton skeleton--rect ${className}`}
        style={{ width, height }}
        aria-hidden="true"
    />
);

export const SkeletonCard = ({ className = '' }) => (
    <div className={`skeleton-card ${className}`} aria-hidden="true">
        <SkeletonRect height="150px" />
        <div className="skeleton-card__content">
            <SkeletonText width="80%" height="1.25rem" />
            <SkeletonText width="60%" />
            <SkeletonText width="40%" />
        </div>
    </div>
);

export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
    <div className="skeleton-table" aria-hidden="true">
        <div className="skeleton-table__header">
            {Array.from({ length: columns }).map((_, i) => (
                <SkeletonText key={i} height="1rem" />
            ))}
        </div>
        {Array.from({ length: rows }).map((_, rowIndex) => (
            <div key={rowIndex} className="skeleton-table__row">
                {Array.from({ length: columns }).map((_, colIndex) => (
                    <SkeletonText key={colIndex} height="0.875rem" />
                ))}
            </div>
        ))}
    </div>
);

export const SkeletonDashboard = () => (
    <div className="skeleton-dashboard" aria-label="Dashboard yükleniyor" role="status">
        <div className="skeleton-dashboard__header">
            <SkeletonText width="200px" height="2rem" />
            <SkeletonText width="150px" height="1rem" />
        </div>
        <div className="skeleton-dashboard__stats">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="skeleton-dashboard__stat-card">
                    <SkeletonCircle size="48px" />
                    <SkeletonText width="60%" height="1.5rem" />
                    <SkeletonText width="40%" />
                </div>
            ))}
        </div>
        <div className="skeleton-dashboard__content">
            <SkeletonRect height="300px" />
        </div>
    </div>
);

export default LoadingSpinner;
